// src/context/UIContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode
} from 'react'
import { useTheme, useMediaQuery } from '@mui/material'

/**
 * Como captura los datos esta terminal:
 * - handheld: lector fisico tipo teclado (HID). No se ofrece la camara.
 * - camera: sin lector fisico, se escanea con la camara del dispositivo.
 */
export type CaptureMode = 'handheld' | 'camera'

interface UIContextType {
  isMobile: boolean
  viewMode: 'short' | 'full'
  isShortMode: boolean
  isCollapsed: boolean
  captureMode: CaptureMode
  setCaptureMode: (mode: CaptureMode) => void
  isCameraCaptureEnabled: boolean
  scanBeep: boolean
  setScanBeep: (enabled: boolean) => void
  isScanBeepEnabled: boolean
  toggleCollapsed: () => void
  toggleMode: () => void
  resetMode: () => void
  isVisible: (visibility?: 'mobile' | 'desktop' | 'both') => boolean
}

const UIContext = createContext<UIContextType | undefined>(undefined)

export const UIProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const theme = useTheme()
  // Detectamos si es pantalla pequeña
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // Leemos si el usuario ya tenía una preferencia guardada, sino, por defecto 'short'
  const [viewMode, setViewMode] = useState<'short' | 'full'>(() => {
    const saved = localStorage.getItem('wms_view_mode')
    return saved === 'full' || saved === 'short' ? saved : 'short'
  })

  // Guardamos la preferencia en el navegador cada vez que cambie
  useEffect(() => {
    localStorage.setItem('wms_view_mode', viewMode)
  }, [viewMode])

  // Estado del menú lateral colapsado (solo iconos). Aplica únicamente en desktop.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    return localStorage.getItem('wms_menu_collapsed') === 'true'
  })

  useEffect(() => {
    localStorage.setItem('wms_menu_collapsed', String(collapsed))
  }, [collapsed])

  // Modo de captura. Por defecto 'handheld': la camara es opt-in, para que nadie
  // vea aparecer un boton que no pidio.
  const [captureMode, setCaptureMode] = useState<CaptureMode>(() => {
    const saved = localStorage.getItem('wms_capture_mode')
    return saved === 'camera' || saved === 'handheld' ? saved : 'handheld'
  })

  // A diferencia de viewMode, este NO se resetea en el logout: describe al equipo
  // (este telefono no tiene lector), no a la sesion.
  useEffect(() => {
    localStorage.setItem('wms_capture_mode', captureMode)
  }, [captureMode])

  // Beep al escanear (perfil > SONIDO DE ESCANEO). Encendido por defecto. Igual
  // que el modo de captura, describe al equipo y no se resetea en el logout.
  const [scanBeep, setScanBeep] = useState<boolean>(() => {
    return localStorage.getItem('wms_scan_beep') !== 'false'
  })

  useEffect(() => {
    localStorage.setItem('wms_scan_beep', String(scanBeep))
  }, [scanBeep])

  const toggleCollapsed = () => {
    setCollapsed((prev) => !prev)
  }

  const toggleMode = () => {
    setViewMode((prev) => (prev === 'short' ? 'full' : 'short'))
  }

  const resetMode = () => {
    setViewMode('short')
  }

  const isVisible = (visibility: 'mobile' | 'desktop' | 'both' = 'desktop') => {
    if (visibility === 'both') return true
    if (visibility === 'mobile') return isMobile
    if (visibility === 'desktop') return !isMobile
    return true
  }

  return (
    <UIContext.Provider
      value={{
        isMobile,
        viewMode,
        // Solo aplica el modo corto si está en un celular Y eligió el modo corto
        isShortMode: isMobile && viewMode === 'short',
        // El colapso a solo iconos solo aplica en desktop
        isCollapsed: !isMobile && collapsed,
        captureMode,
        setCaptureMode,
        // Unico lugar donde vive la regla "la camara es solo para mobile":
        // los consumidores no repiten el chequeo de isMobile.
        isCameraCaptureEnabled: isMobile && captureMode === 'camera',
        scanBeep,
        setScanBeep,
        // Mismo criterio que la camara: el beep es solo para mobile.
        isScanBeepEnabled: isMobile && scanBeep,
        toggleCollapsed,
        toggleMode,
        resetMode,
        isVisible
      }}
    >
      {children}
    </UIContext.Provider>
  )
}

// Hook personalizado para usar en cualquier componente
export const useUIMode = () => {
  const context = useContext(UIContext)
  if (!context) {
    throw new Error('useUIMode debe ser usado dentro de un UIProvider')
  }
  return context
}
